import type { Role } from '@/stores/authStore';

/** Roles asignables a una cuenta (el `null` del tipo Role es "todavía no cargado"). */
export type RolAsignable = Exclude<Role, null>;

/** Cómo se nombra cada rol en la interfaz. */
export const ETIQUETA_ROL: Record<RolAsignable, string> = {
    superadmin: 'Super Admin',
    admin: 'Administración',
    rrhh: 'RRHH',
    supervisor: 'Supervisor',
    facturador: 'Facturador',
    funcionario: 'Funcionario',
};

/**
 * La columna `profiles.rol` es `text` en la base, no un enum de Postgres, así que
 * lo que llega es un `string` cualquiera. Esto lo estrecha en el borde: un valor
 * que no sea un rol conocido se trata como "sin rol" en vez de colarse por un cast.
 */
export function esRolValido(valor: string | null | undefined): valor is RolAsignable {
    return valor != null && Object.hasOwn(ETIQUETA_ROL, valor);
}

/**
 * Qué roles pueden abrir cada pantalla.
 *
 * Única fuente de verdad: la consumen el menú lateral y las guardas de ruta, así
 * que no pueden quedar desalineados. Ocultar una entrada del menú no protege
 * nada por sí solo; la guarda es la que impide entrar escribiendo la URL.
 *
 * El reparto separa dos áreas que se ven entre sí pero no se pisan:
 *   - RRHH gestiona personal, horarios y asistencia.
 *   - Administración gestiona clientes, servicios y la configuración.
 * Cada una puede consultar el módulo de la otra. La restricción de escritura la
 * imponen las políticas RLS, no esta tabla.
 *
 * `facturador` no figura en ninguna pantalla: quedó sin función cuando la
 * facturación pasó a calcularse desde horarios, y las tablas `facturas` y
 * `factura_items` dejaron de usarse en la interfaz.
 */
export const ACCESO_PANTALLA = {
    dashboard: ['superadmin', 'admin', 'rrhh'],
    funcionarios: ['superadmin', 'admin', 'rrhh', 'supervisor'],
    horarios: ['superadmin', 'admin', 'rrhh', 'supervisor', 'funcionario'],
    asistencia: ['superadmin', 'admin', 'rrhh', 'supervisor', 'funcionario'],
    servicios: ['superadmin', 'admin', 'rrhh', 'supervisor'],
    logistica: ['superadmin', 'admin', 'rrhh', 'supervisor'],
    clientes: ['superadmin', 'admin', 'rrhh', 'supervisor'],
    reportes: ['superadmin', 'admin', 'rrhh'],
    nomina: ['superadmin', 'admin', 'rrhh'],
    facturacion: ['superadmin', 'admin'],
    configuracion: ['superadmin', 'admin'],
} as const satisfies Record<string, readonly RolAsignable[]>;

export type Pantalla = keyof typeof ACCESO_PANTALLA;

/** Si el rol todavía no se resolvió se deniega: ante la duda, no mostrar. */
export function puedeVer(role: Role, pantalla: Pantalla): boolean {
    return role !== null && (ACCESO_PANTALLA[pantalla] as readonly Role[]).includes(role);
}
